import React, { useState } from "react";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  ListPageShell,
  useListFilter,
} from "@/components/shared/ListPageShell";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2, ExternalLink } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { formatNumber } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { WasteMatrixFormModal } from "./WasteMatrixFormModal";
import {
  InlineFilterRow,
  type FilterField,
} from "@/components/shared/InlineFilterRow";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { useExcelExport } from "@/hooks/useExcelExport";
import { type ExcelColumnDef } from "@/lib/excel.utils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const WASTE_FILTER_FIELDS: (FilterField | null)[] = [
  { key: "processName", label: "Lọc công đoạn...", type: "text" },
  { key: "minQuantity", label: "Lọc MOQ...", type: "text" },
  { key: "wastePercentage", label: "Lọc %...", type: "text" },
  { key: "fixedWaste", label: "Lọc bù hao...", type: "text" },
  {
    key: "statusKey",
    label: "Trạng thái",
    type: "select",
    options: [
      { label: "Hoạt động", value: "HOAT_DONG" },
      { label: "Tạm dừng", value: "TAM_DUNG" },
    ],
  },
  {
    key: "sourceKey",
    label: "Nguồn",
    type: "select",
    options: [
      { label: "Quy trình SX", value: "process" },
      { label: "Bậc MOQ riêng", value: "manual" },
    ],
  },
  null,
];

const WASTE_ACTIVE_FILTER_FIELDS: FilterField[] = WASTE_FILTER_FIELDS.filter(
  (f): f is FilterField => Boolean(f && f.key),
);

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "processCode", label: "Mã Công Đoạn", width: 16 },
  { key: "processName", label: "Tên Công Đoạn", width: 25 },
  // Nhóm vật tư + MOQ từ / đến là khoá của một bậc hao hụt (05/11/2026) — xuất đủ để phân biệt các bậc
  { key: "materialGroup", label: "Nhóm Vật Tư", width: 16 },
  { key: "minQuantity", label: "MOQ (SL Tối Thiểu)", width: 18, format: "number" },
  { key: "moqTo", label: "MOQ Đến", width: 12, format: "number" },
  { key: "wastePercentage", label: "Phần Trăm Hao Hụt (%)", width: 22, format: "decimal" },
  { key: "fixedWaste", label: "Hao Hụt Cố Định (Bù hao)", width: 22, format: "number" },
  { key: "unit", label: "Đơn Vị", width: 10 },
  { key: "statusLabel", label: "Trạng Thái", width: 14 },
  { key: "source", label: "Nguồn", width: 16 },
];

// Dòng gốc (source = PROCESS) lấy từ Quy trình SX, tự cập nhật khi sửa công đoạn; ma trận chỉ để tính giá nhanh
const isProcessRow = (r: any) => r?.source === "PROCESS";
const wasteUnitLabel = (r: any) => (r?.process?.wasteUnit === "PCS" ? "cái" : "tờ");

export default function WasteMatrixList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();
  const canCreate = hasPermission("waste-matrix", "create");
  const canEdit = hasPermission("waste-matrix", "update");
  const canDelete = hasPermission("waste-matrix", "delete");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [viewingItem, setViewingItem] = useState<any | null>(null);

  const { data: matrixItems, isLoading } = useQuery({
    queryKey: ["waste-matrix"],
    queryFn: () => trpcClient.wasteMatrix.list.query({}),
  });

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "Xóa cấu hình hao hụt?",
      message: "Bạn có chắc chắn muốn xóa cấu hình bù hao này không?",
      confirmText: "Xóa",
      cancelText: "Hủy",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await trpcClient.wasteMatrix.delete.mutate({ id });
      toast.success("Xóa cấu hình hao hụt thành công");
      queryClient.invalidateQueries({ queryKey: ["waste-matrix"] });
    } catch (err: any) {
      toast.error(err?.message || "Xóa thất bại");
    }
  };

  // API trả mảng (findMany): trước đây đọc .items nên danh sách luôn trống dù đã lưu (FB-CI88PW)
  // Tên trường API (moqFrom / wastePercent / wasteFixed) → tên trang dùng; kèm khoá lọc tên công đoạn / trạng thái / nguồn
  const rawItems: any[] = React.useMemo(
    () =>
      (Array.isArray(matrixItems) ? matrixItems : (matrixItems as any)?.items || []).map((r: any) => {
        const active = r.isActive !== false;
        return {
          ...r,
          minQuantity: Number(r.minQuantity ?? r.moqFrom ?? 0),
          wastePercentage: Number(r.wastePercentage ?? r.wastePercent ?? 0),
          fixedWaste: Number(r.fixedWaste ?? r.wasteFixed ?? 0),
          isActive: active,
          statusKey: active ? "HOAT_DONG" : "TAM_DUNG",
          processName: r.process?.name || r.processId || "",
          sourceKey: isProcessRow(r) ? "process" : "manual",
        };
      }),
    [matrixItems],
  );

  const {
    filtered: displayItems,
    paginatedItems,
    totalItems,
    currentPage,
    pageSize,
    showFilter,
    filterValues,
    onFilterChange,
    onToggleFilter,
    onClearAll,
    onPageChange,
    onPageSizeChange,
  } = useListFilter({
    data: rawItems,
    searchFields: ["process.name", "process.stepId", "processName", "processId"],
  });

  const exportData = React.useMemo(() => {
    return displayItems.map((item: any) => ({
      id: item.id,
      // Mã công đoạn theo mã CĐ, không xuất id nội bộ khi thiếu (05/11/2026)
      processCode: item.process?.stepId || "",
      processName: item.process?.name || "",
      materialGroup: item.materialGroup || "",
      minQuantity: item.minQuantity,
      moqTo: item.moqTo ?? "",
      wastePercentage: item.wastePercentage,
      fixedWaste: item.fixedWaste,
      unit: wasteUnitLabel(item),
      statusLabel: item.isActive ? "Hoạt động" : "Tạm dừng",
      source: isProcessRow(item) ? "Quy trình SX" : "Bậc MOQ riêng",
    }));
  }, [displayItems]);

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "Dinh_Muc_Hao_Hut_San_Xuat",
    localData: exportData,
  });

  return (
    <>
      <ListPageShell
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        filterValues={filterValues}
        filterFields={WASTE_ACTIVE_FILTER_FIELDS}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        actions={
          <div className="flex items-center gap-2">
            <ExportImportButtons
              onExport={() => handleExport()}
              exportLoading={isExporting}
            />
            {canCreate && (
              <Button
                size="sm"
                onClick={() => {
                  setEditingItem(null);
                  setIsModalOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Thêm Cấu Hình
              </Button>
            )}
          </div>
        }
      >
        <Card className="border-none shadow-none rounded-none p-0 overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50 border-b border-slate-200">
              <TableRow>
                <TableHead className="apv-col-code font-semibold text-slate-700">
                  Công Đoạn
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  MOQ (SL Tối Thiểu)
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  % Hao Hụt
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  Hao Hụt Cố Định
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-center">
                  Trạng Thái
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-center">
                  Nguồn
                </TableHead>
                <TableHead className="apv-col-actions w-[100px] text-right">Thao tác</TableHead>
              </TableRow>
              {showFilter && (
                <InlineFilterRow
                  columns={WASTE_FILTER_FIELDS as any}
                  filterFields={WASTE_ACTIVE_FILTER_FIELDS}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Đang tải...
                  </TableCell>
                </TableRow>
              ) : displayItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-slate-500"
                  >
                    Chưa có cấu hình nào
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((item: any) => (
                  <TableRow
                    key={item.id}
                    className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                    onClick={() => setViewingItem(item)}
                  >
                    <TableCell className="apv-col-code font-medium text-slate-900">
                      {item.process?.name || item.processId}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(item.minQuantity)}
                    </TableCell>
                    <TableCell className="text-right text-rose-600 font-medium">
                      {item.wastePercentage}%
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(item.fixedWaste)} {wasteUnitLabel(item)}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.isActive ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                          Hoạt động
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500 bg-slate-50 border-slate-200">
                          Tạm dừng
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {isProcessRow(item) ? (
                        <Badge variant="secondary" data-testid="waste-source-process">Quy trình SX</Badge>
                      ) : (
                        <Badge variant="outline">Bậc MOQ riêng</Badge>
                      )}
                    </TableCell>
                    <TableCell
                      className="apv-col-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end gap-2">
                        {!isProcessRow(item) && canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Xóa cấu hình"
                            title="Xóa cấu hình"
                            className="h-8 w-8 text-slate-500 hover:text-rose-600"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </ListPageShell>

      {/* View Detail Dialog */}
      <Dialog
        open={!!viewingItem}
        onOpenChange={(open) => !open && setViewingItem(null)}
      >
        <DialogContent className="sm:max-w-[480px] z-[10000]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle>
                Chi Tiết Cấu Hình Hao Hụt
              </DialogTitle>
              <Badge variant={isProcessRow(viewingItem) ? "secondary" : "outline"}>
                {isProcessRow(viewingItem) ? "Quy trình SX" : "Bậc MOQ riêng"}
              </Badge>
            </div>
            <DialogDescription className="text-xs text-slate-500">
              {isProcessRow(viewingItem)
                ? "Lấy từ Quy trình SX, tự cập nhật khi sửa công đoạn. Dùng để tính giá nhanh."
                : "Bậc MOQ riêng cho tính giá nhanh, áp dụng khi số lượng từ MOQ này trở lên."}
            </DialogDescription>
          </DialogHeader>

          {viewingItem && (
            <div className="space-y-3 py-2 text-xs">
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-1">
                  Công đoạn áp dụng
                </span>
                <span className="font-bold text-slate-900 text-sm">
                  {viewingItem.process?.name || viewingItem.processId}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase block">
                    MOQ (Tối thiểu)
                  </span>
                  <span className="text-base font-mono font-black text-slate-800">
                    {formatNumber(viewingItem.minQuantity)}
                  </span>
                </div>
                <div className="bg-rose-50/70 border border-rose-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-rose-700 uppercase block">
                    % Hao hụt
                  </span>
                  <span className="text-base font-mono font-black text-rose-700">
                    {viewingItem.wastePercentage}%
                  </span>
                </div>
                <div className="bg-amber-50/70 border border-amber-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-amber-700 uppercase block">
                    Hao hụt bù hao
                  </span>
                  <span className="text-base font-mono font-black text-amber-700">
                    {formatNumber(viewingItem.fixedWaste)} {wasteUnitLabel(viewingItem)}
                  </span>
                </div>
              </div>

              <div className="p-3 bg-blue-50/60 border border-blue-200/80 rounded-lg text-blue-900 text-[11px] leading-relaxed">
                <strong>Công thức tính:</strong> Tổng phế phẩm dự toán = (Số lượng đặt x {viewingItem.wastePercentage}%) + {formatNumber(viewingItem.fixedWaste)} {wasteUnitLabel(viewingItem)} cho mỗi lần chạy máy.
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setViewingItem(null)}
            >
              Đóng
            </Button>
            {isProcessRow(viewingItem) ? (
              <Button
                size="sm"
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                data-testid="waste-open-process"
                onClick={() => {
                  const id = viewingItem.processId;
                  setViewingItem(null);
                  navigate({ to: "/processes/$processId", params: { processId: id } });
                }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Sửa công đoạn
              </Button>
            ) : canEdit ? (
              <Button
                size="sm"
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                onClick={() => {
                  setEditingItem(viewingItem);
                  setIsModalOpen(true);
                  setViewingItem(null);
                }}
              >
                <Edit2 className="w-3.5 h-3.5" />
                Chỉnh Sửa
              </Button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <WasteMatrixFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        item={editingItem}
      />
    </>
  );
}
